/**
 * Bound URL contract (docs/security/injection-quarantine.md, rule Q3).
 *
 * A URL loads only when code chose its exact bytes and the server signed
 * them. Model output, model tool arguments and model-written props never bind
 * a URL. Two signed artifacts:
 *
 * - `BoundUrl`: long-lived. Shows as a link and loads on a trusted user
 *   gesture. A tapped bound link loads at once.
 * - `AutoOpenGrant`: short-lived, one per user action (`reactId`). Lets the
 *   agent open a page with no tap. Only the server mints it.
 *
 * Signing lives on the server. This module holds the shared shapes, the
 * canonical signed bytes and verification, so the server and every client
 * sign and verify the same bytes.
 */

export const URL_SOURCE_CLASSES = [
  "template_clean",
  "provider_canonical",
  "search_result",
  "outside_text",
  "user_typed",
] as const;
export type UrlSourceClass = (typeof URL_SOURCE_CLASSES)[number];

export type BoundUrl = Readonly<{
  kind: "bound_url";
  url: string;
  sourceClass: UrlSourceClass;
  kid: string;
  /** base64url Ed25519 signature over `boundUrlSignedBytes`. */
  sig: string;
  /**
   * Present when the server bound a URL template rather than one URL: `url`
   * is then any URL that matches it (matchesBoundUrlTemplate), and the
   * signature covers the template, not `url`.
   */
  template?: BoundUrlTemplate;
}>;

/**
 * One query parameter a client may fill in a bound template, with the only
 * values it may take. Code on the server chose the parameter and its values;
 * nothing outside writes them.
 * - `int`: a decimal integer from `min` to `max`.
 * - `enum`: one of `values`, exactly.
 * - `date_time`: `${date}THH:MM` (24-hour) on the one `date` (YYYY-MM-DD).
 */
export type BoundUrlTemplateParam =
  | Readonly<{ name: string; type: "int"; min: number; max: number }>
  | Readonly<{ name: string; type: "enum"; values: readonly string[] }>
  | Readonly<{ name: string; type: "date_time"; date: string }>;

/**
 * A URL with typed holes: the origin, path, fragment and every other query
 * parameter of `base` stay as they are, and each listed parameter may be
 * absent, keep its value in `base`, or take one of its allowed values.
 */
export type BoundUrlTemplate = Readonly<{ base: string; params: readonly BoundUrlTemplateParam[] }>;

export type AutoOpenGrant = Readonly<{
  kind: "auto_open_grant";
  kid: string;
  userId: string;
  reactId: string;
  actionId: string;
  url: string;
  sourceClass: UrlSourceClass;
  /** Expiry, epoch milliseconds. */
  exp: number;
  /** base64url Ed25519 signature over `autoOpenGrantSignedBytes`. */
  sig: string;
}>;

/** Longest lifetime the server may give a grant. */
export const AUTO_OPEN_GRANT_MAX_TTL_MS = 2 * 60_000;
export const BOUND_URL_MAX_LENGTH = 2_048;

/**
 * Source classes the agent may open with no tap. Before planner isolation
 * (Q4) only code-chosen bytes qualify. `outside_text` never qualifies: an
 * auto-loaded email link is a read receipt and leaks the user's IP.
 */
export function sourceClassAutoOpens(
  sourceClass: UrlSourceClass,
  plannerIsolated: boolean,
): boolean {
  switch (sourceClass) {
    case "template_clean":
    case "provider_canonical":
    case "user_typed":
      return true;
    case "search_result":
      return plannerIsolated;
    case "outside_text":
      return false;
  }
}

export function isUrlSourceClass(value: unknown): value is UrlSourceClass {
  return typeof value === "string" && (URL_SOURCE_CLASSES as readonly string[]).includes(value);
}

/** The exact bytes a bound URL signs, or null when the URL may never bind. */
export function normalizeBindableUrl(raw: string): string | null {
  if (raw.length > BOUND_URL_MAX_LENGTH) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || url.username || url.password) return null;
  return url.href;
}

// A host of plain ASCII labels and an optional port: no userinfo, no percent
// escapes, no Unicode (an IDN arrives as punycode or not at all).
const CANONICAL_URL_SHAPE = /^(?:(https?):\/\/)?([a-z0-9.-]+(?::\d{1,5})?)([/?#].*)?$/i;
// Whitespace, controls and backslashes: the WHATWG parser strips or rewrites
// them, and other parsers (Python's urlsplit, which SerpAPI-style vendors
// use) do not, so the two can read different hosts from one string.
const PARSER_DIVERGENT_CHARACTER = /[\u0000- \u007f-\u009f\\]/;

/**
 * The WHATWG href of a URL value that every common parser reads the same way,
 * or null. The scheme and authority must already be the href's, apart from a
 * missing `https://` scheme and host letter case: byte for byte, so the host
 * the WHATWG parser reads is the host Python's urlsplit reads. The path,
 * query and fragment may differ from the href only by the parser's own
 * percent-encoding (`/wiki/Köln` -> `/wiki/K%C3%B6ln`, `?q=don't` ->
 * `?q=don%27t`) and a missing `/` path. Anything else
 * (`https://ok.com\@collector.example`, a tab or newline inside, surrounding
 * spaces, userinfo, a percent-escaped or Unicode host, IPv4 shorthand, a dot
 * segment, a default port) is refused rather than repaired: a value that
 * needs repair is not one code or the user produced. Callers fetch the
 * returned href, never the raw value.
 */
export function canonicalHttpUrl(value: unknown): string | null {
  if (typeof value !== "string" || value.length > BOUND_URL_MAX_LENGTH) return null;
  if (PARSER_DIVERGENT_CHARACTER.test(value)) return null;
  const match = CANONICAL_URL_SHAPE.exec(value);
  if (!match) return null;
  const [, scheme, authority, rest = ""] = match;
  let href: string;
  try {
    href = new URL(scheme ? value : `https://${value}`).href;
  } catch {
    return null;
  }
  const origin = `${(scheme ?? "https").toLowerCase()}://${authority.toLowerCase()}`;
  const parsed = new URL(href);
  const encodedRest = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  if (href !== `${origin}${encodedRest}`) return null;
  return differsOnlyByPercentEncoding(rest.startsWith("/") ? rest : `/${rest}`, encodedRest) ? href : null;
}

const LONE_SURROGATE = /^[\uD800-\uDFFF]$/;

/**
 * True when `encoded` is `raw` with some characters replaced by the
 * uppercase percent-escapes of their UTF-8 bytes, which is all the WHATWG
 * parser does to a path, query or fragment that has no dot segment or
 * backslash. A raw `%` is never one the parser added, so it must match as is.
 */
function differsOnlyByPercentEncoding(raw: string, encoded: string): boolean {
  let at = 0;
  for (const char of raw) {
    if (encoded.startsWith(char, at)) {
      at += char.length;
      continue;
    }
    // The parser writes a lone surrogate as U+FFFD: a change, not an escape.
    if (char === "%" || LONE_SURROGATE.test(char)) return false;
    let escape = "";
    for (const byte of new TextEncoder().encode(char)) escape += `%${byte.toString(16).toUpperCase().padStart(2, "0")}`;
    if (!encoded.startsWith(escape, at)) return false;
    at += escape.length;
  }
  return at === encoded.length;
}

// Fields are joined with a separator no field may contain, so no two
// different payloads share signed bytes.
const FIELD_SEPARATOR = "\n";

function joinFields(fields: readonly string[]): Uint8Array<ArrayBuffer> | null {
  if (fields.some((field) => field.includes(FIELD_SEPARATOR))) return null;
  return new TextEncoder().encode(fields.join(FIELD_SEPARATOR));
}

export const BOUND_URL_TEMPLATE_MAX_PARAMS = 8;
export const BOUND_URL_TEMPLATE_MAX_ENUM_VALUES = 24;
export const BOUND_URL_TEMPLATE_MAX_VALUE_LENGTH = 200;
/**
 * The most UTF-8 bytes a template's base and canonical text may take, so a
 * templated binding with a full-length `url` stays inside the 8 KB
 * link-check and grant request bodies and the desktop's 8 KB `bound=`
 * parameter (base64url).
 */
export const BOUND_URL_TEMPLATE_MAX_BYTES = 3_072;

const TEMPLATE_PARAM_NAME = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
const TEMPLATE_DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const TEMPLATE_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const TEMPLATE_INT = /^(0|[1-9]\d{0,6})$/;
const TEMPLATE_INT_MAX = 9_999_999;
const CONTROL_CHARACTER = /[\u0000-\u001f\u007f-\u009f]/;

function canonicalTemplateParam(value: unknown): unknown[] | null {
  if (!value || typeof value !== "object") return null;
  const param = value as Record<string, unknown>;
  if (typeof param.name !== "string" || !TEMPLATE_PARAM_NAME.test(param.name)) return null;
  switch (param.type) {
    case "int": {
      const { min, max } = param;
      if (!Number.isSafeInteger(min) || !Number.isSafeInteger(max)) return null;
      if ((min as number) < 0 || (min as number) > (max as number) || (max as number) > TEMPLATE_INT_MAX) return null;
      return ["int", param.name, min, max];
    }
    case "enum": {
      const values = param.values;
      if (!Array.isArray(values) || values.length === 0 || values.length > BOUND_URL_TEMPLATE_MAX_ENUM_VALUES) return null;
      const valid = values.every((item) => typeof item === "string" && item.length > 0
        && item.length <= BOUND_URL_TEMPLATE_MAX_VALUE_LENGTH && !CONTROL_CHARACTER.test(item));
      if (!valid || new Set(values).size !== values.length) return null;
      return ["enum", param.name, ...[...(values as string[])].sort()];
    }
    case "date_time":
      return typeof param.date === "string" && TEMPLATE_DATE.test(param.date) ? ["date_time", param.name, param.date] : null;
    default:
      return null;
  }
}

/**
 * The canonical text of a template the server may sign, or null when it is
 * malformed or too large: an https `base` in its normalized form, one to
 * eight distinct parameters with valid names and value rules. Parameters and
 * enum values are sorted, so equal templates always sign the same bytes.
 */
export function canonicalBoundUrlTemplate(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const template = value as Record<string, unknown>;
  if (typeof template.base !== "string" || normalizeBindableUrl(template.base) !== template.base) return null;
  const params = template.params;
  if (!Array.isArray(params) || params.length === 0 || params.length > BOUND_URL_TEMPLATE_MAX_PARAMS) return null;
  const canonical: unknown[][] = [];
  for (const param of params) {
    const entry = canonicalTemplateParam(param);
    if (!entry) return null;
    canonical.push(entry);
  }
  canonical.sort((left, right) => (String(left[1]) < String(right[1]) ? -1 : 1));
  if (new Set(canonical.map((entry) => entry[1])).size !== canonical.length) return null;
  const text = JSON.stringify(canonical);
  const size = new TextEncoder().encode(text).length + template.base.length;
  return size <= BOUND_URL_TEMPLATE_MAX_BYTES ? text : null;
}

function templateValueAllowed(param: BoundUrlTemplateParam, value: string): boolean {
  switch (param.type) {
    case "int":
      return TEMPLATE_INT.test(value) && Number(value) >= param.min && Number(value) <= param.max;
    case "enum":
      return param.values.includes(value);
    case "date_time":
      return value.length === 16 && value.startsWith(`${param.date}T`) && TEMPLATE_TIME.test(value.slice(11));
  }
}

/** The query string `URLSearchParams` writes for these entries (form encoding). */
function formQuery(entries: readonly (readonly [string, string])[]): string {
  const query = new URLSearchParams(entries.map(([name, value]) => [name, value])).toString();
  return query ? `?${query}` : "";
}

/**
 * True when `url` is `template.base` with only the template's parameters
 * changed: same origin, path and fragment; every other query parameter
 * unchanged and in order; each template parameter at most once, holding its
 * value in `base` or an allowed value. The query must be in the form
 * encoding `URLSearchParams` writes, so no two spellings of one URL exist.
 */
export function matchesBoundUrlTemplate(template: BoundUrlTemplate, url: string): boolean {
  const concrete = normalizeBindableUrl(url);
  if (!concrete || concrete !== url || canonicalBoundUrlTemplate(template) === null) return false;
  if (concrete === template.base) return true;
  const base = new URL(template.base);
  const target = new URL(concrete);
  if (target.origin !== base.origin || target.pathname !== base.pathname || target.hash !== base.hash) return false;
  const targetEntries = [...target.searchParams.entries()];
  if (target.search !== formQuery(targetEntries)) return false;
  const params = new Map(template.params.map((param) => [param.name, param]));
  const baseEntries = [...base.searchParams.entries()];
  const fixed = (entries: readonly (readonly [string, string])[]) => entries.filter(([name]) => !params.has(name));
  if (formQuery(fixed(targetEntries)) !== formQuery(fixed(baseEntries))) return false;
  const seen = new Set<string>();
  for (const [name, value] of targetEntries) {
    const param = params.get(name);
    if (!param) continue;
    if (seen.has(name)) return false;
    seen.add(name);
    const baseValues = base.searchParams.getAll(name);
    const keepsBase = baseValues.length === 1 && baseValues[0] === value;
    if (!keepsBase && !templateValueAllowed(param, value)) return false;
  }
  return true;
}

/** The binding of one concrete `url` a verified templated binding allows, or null when it does not match. */
export function boundUrlFromTemplate(binding: BoundUrl, url: string): BoundUrl | null {
  if (!binding.template || !matchesBoundUrlTemplate(binding.template, url)) return null;
  return { ...binding, url };
}

export function boundUrlSignedBytes(input: {
  kid: string;
  userId: string;
  sourceClass: UrlSourceClass;
  url: string;
  template?: BoundUrlTemplate;
}): Uint8Array<ArrayBuffer> | null {
  const url = normalizeBindableUrl(input.url);
  if (!url || url !== input.url) return null;
  if (input.template === undefined) {
    return joinFields(["sm-bound-url/v1", input.kid, input.userId, input.sourceClass, url]);
  }
  const canonical = canonicalBoundUrlTemplate(input.template);
  if (!canonical || !matchesBoundUrlTemplate(input.template, url)) return null;
  return joinFields(["sm-bound-url-template/v1", input.kid, input.userId, input.sourceClass, input.template.base, canonical]);
}

export function autoOpenGrantSignedBytes(input: Omit<AutoOpenGrant, "kind" | "sig">): Uint8Array<ArrayBuffer> | null {
  const url = normalizeBindableUrl(input.url);
  if (!url || url !== input.url || !Number.isSafeInteger(input.exp)) return null;
  return joinFields([
    "sm-auto-open/v1",
    input.kid,
    input.userId,
    input.reactId,
    input.actionId,
    input.sourceClass,
    String(input.exp),
    url,
  ]);
}

export function encodeBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

export function decodeBase64Url(value: string): Uint8Array<ArrayBuffer> | null {
  if (!/^[A-Za-z0-9_-]*$/.test(value)) return null;
  const padded = value.replaceAll("-", "+").replaceAll("_", "/") + "=".repeat((4 - (value.length % 4)) % 4);
  try {
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return bytes;
  } catch {
    return null;
  }
}

/** Raw 32-byte Ed25519 public keys by key id, base64url. */
export type BoundUrlPublicKeys = Readonly<Record<string, string>>;

const importedKeys = new Map<string, Promise<CryptoKey | null>>();

function importPublicKey(rawBase64Url: string): Promise<CryptoKey | null> {
  let key = importedKeys.get(rawBase64Url);
  if (!key) {
    const raw = decodeBase64Url(rawBase64Url);
    key = raw && raw.length === 32
      ? crypto.subtle.importKey("raw", raw, { name: "Ed25519" }, false, ["verify"]).catch(() => null)
      : Promise.resolve(null);
    importedKeys.set(rawBase64Url, key);
  }
  return key;
}

async function verifySignature(
  keys: BoundUrlPublicKeys,
  kid: string,
  sig: string,
  bytes: Uint8Array<ArrayBuffer> | null,
): Promise<boolean> {
  const rawKey = Object.hasOwn(keys, kid) ? keys[kid] : undefined;
  const signature = decodeBase64Url(sig);
  if (!bytes || !rawKey || !signature || signature.length !== 64) return false;
  const key = await importPublicKey(rawKey);
  if (!key) return false;
  try {
    return await crypto.subtle.verify("Ed25519", key, signature, bytes);
  } catch {
    return false;
  }
}

export function isBoundUrlShape(value: unknown): value is BoundUrl {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return candidate.kind === "bound_url"
    && typeof candidate.url === "string"
    && isUrlSourceClass(candidate.sourceClass)
    && typeof candidate.kid === "string"
    && typeof candidate.sig === "string"
    && (candidate.template === undefined || canonicalBoundUrlTemplate(candidate.template) !== null);
}

export function isAutoOpenGrantShape(value: unknown): value is AutoOpenGrant {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return candidate.kind === "auto_open_grant"
    && typeof candidate.kid === "string"
    && typeof candidate.userId === "string"
    && typeof candidate.reactId === "string"
    && typeof candidate.actionId === "string"
    && typeof candidate.url === "string"
    && isUrlSourceClass(candidate.sourceClass)
    && typeof candidate.exp === "number"
    && typeof candidate.sig === "string";
}

/**
 * True only when the server signed exactly this URL and class for this
 * user, or signed a template for them that this URL matches.
 */
export function verifyBoundUrl(
  value: unknown,
  userId: string,
  keys: BoundUrlPublicKeys,
): Promise<boolean> {
  if (!isBoundUrlShape(value)) return Promise.resolve(false);
  return verifySignature(keys, value.kid, value.sig, boundUrlSignedBytes({
    kid: value.kid,
    userId,
    sourceClass: value.sourceClass,
    url: value.url,
    template: value.template,
  }));
}

/**
 * True only for an unexpired grant the server signed for this user. Callers
 * still enforce single use per `reactId`.
 */
export function verifyAutoOpenGrant(
  value: unknown,
  userId: string,
  keys: BoundUrlPublicKeys,
  now: number = Date.now(),
): Promise<boolean> {
  if (!isAutoOpenGrantShape(value) || value.userId !== userId) return Promise.resolve(false);
  if (value.exp <= now || value.exp > now + AUTO_OPEN_GRANT_MAX_TTL_MS) return Promise.resolve(false);
  const { kind: _kind, sig, ...fields } = value;
  return verifySignature(keys, value.kid, sig, autoOpenGrantSignedBytes(fields));
}
